// src/graphql/queries/tenderQueries.js
import { tenderFields, assessmentFields, documentFields } from '../fragments';

export const getTenders = () => `query GetTenders($status: TenderStatus, $limit: Int, $offset: Int) {
    tenders(status: $status, limit: $limit, offset: $offset) {
        ${tenderFields}
        documents {
            ${documentFields}
        }
    }
}`;

export const getTender = () => `query GetTender($id: String!) {
    tender(id: $id) {
        ${tenderFields}
        documents {
            id
            title
            sourceUrl
            fileType
            fileSize
            filePath
            isIndexed
            createdAt
        }
    }
}`;

export const getAssessmentByTender = () => `query GetTenderAssessment($tenderId: String!) {
    tenderAssessment(tenderId: $tenderId) {
        ${assessmentFields}
    }
}`;

export const searchTenders = () => `query SearchTenders($input: SearchTendersInput!) {
    searchTenders(input: $input) {
        items {
            tender {
                id
                externalId
                title
                description
                type
                rootCpv
                estimatedValue
                currency
                procedureType
                isFramework
                hasOptions
                publicationDate
                location {
                    address
                    city
                    state
                    postalCode
                    country
                    nutsCode
                    latitude
                    longitude
                }
                url
                deadline
                startDate
                endDate
                status
                crawlStatus
                crawlError
                lastCrawled
                lastUpdated
                createdAt
                latitude
                longitude
                documents {
                    id
                    title
                    fileType
                    fileSize
                    isIndexed
                }
            }
            rating {
                id
                tenderId
                companyId
                status
                tags
                rating
                notes
            }
            assessment {
                id
                tenderId
                companyId
                overallScore
                pipeline
                assessedAt
                isOutdated
            }
        }
        totalCount
        pageInfo {
            hasNextPage
            hasPreviousPage
            limit
            offset
        }
    }
}`;

export const getTopRatedTenders = () => `query GetTopRatedTenders($minScore: Int, $maxScore: Int, $showArchived: Boolean, $limit: Int, $offset: Int) {
    topRatedTenders(minScore: $minScore, maxScore: $maxScore, showArchived: $showArchived, limit: $limit, offset: $offset) {
        items {
            tender {
                id
                externalId
                title
                description
                type
                rootCpv
                estimatedValue
                currency
                deadline
                status
                crawlStatus
                createdAt
            }
            rating {
                id
                status
                rating
                notes
            }
            assessment {
                id
                overallScore
                pipeline
                assessedAt
                isOutdated
            }
        }
        totalCount
        pageInfo {
            hasNextPage
            hasPreviousPage
            limit
            offset
        }
    }
}`;

export const getCompanyTenderRating = () => `query GetCompanyTenderRating($tenderId: String!, $companyId: String!) {
    companyTenderRating(tenderId: $tenderId, companyId: $companyId) {
        id
        tenderId
        companyId
        status
        tags
        rating
        notes
    }
}`;

export const getTenderAssessments = () => `query GetTenderAssessments($minScore: Int, $maxScore: Int, $limit: Int, $offset: Int) {
    tenderAssessments(minScore: $minScore, maxScore: $maxScore, limit: $limit, offset: $offset) {
        ${assessmentFields}
    }
}`;

export const getTenderAssessmentsCount = () => `query GetTenderAssessmentsCount($minScore: Int, $maxScore: Int) {
    tenderAssessmentsCount(minScore: $minScore, maxScore: $maxScore)
}`;

export const searchTendersFTS = () => `query SearchTendersFTS($query: String!, $limit: Int, $includeArchived: Boolean) {
    searchTendersFTS(query: $query, limit: $limit, includeArchived: $includeArchived) {
        tenderId
        title
        rank
        snippet
    }
}`;

export const getEvidenceImages = () => `query GetEvidenceImages($documentId: String!, $assessmentId: String!) {
    getEvidenceImages(documentId: $documentId, assessmentId: $assessmentId) {
        documentId
        assessmentId
        status
        fullPageImages {
            page
            imageUrl
            width
            height
            matches {
                evidenceId
                x
                y
                width
                height
                polarity
            }
        }
        snippetImages {
            evidenceId
            imageUrl
            page
            polarity
            criterionLabel
            criterionId
            snippet
        }
        generatedAt
        error
    }
}`;

// LLM narrative assessment — consultant-style tender read (generated by
// generate_narrative_assessment jobs, nightly bulk for the whole corpus).
// Tender-scoped, shared across companies. Feature-gated (`narrative_assessment`)
// on the caller's company — surface "feature disabled" errors gracefully.
export const getTenderNarrativeAssessment = () => `query GetTenderNarrativeAssessment($tenderId: String!, $language: String!) {
    tenderNarrativeAssessment(tenderId: $tenderId, language: $language) {
        id
        tenderId
        language
        promptVersion
        model
        summary
        summaryShort
        scopeAndDeliverables
        technicalRequirements
        businessRequirements
        timelineShape
        stakeholders {
            role
            description
        }
        effortDrivers {
            label
            count
            notes
        }
        risks {
            severity
            description
        }
        openQuestions
        assumptions
        domainPrimary
        domainSecondary
        taxonomyTags
        requiredSkills
        requiredCertifications
        contractForm
        isFramework
        requiresLocalPresence
        evaluationCriteria {
            name
            weight
        }
        exclusionCriteria
        budgetEstimateEUR
        durationMonths
        inputTokens
        outputTokens
        processingTimeMs
        generatedAt
    }
}`;

// LLM facet analysis — grounded answers to the canonical bidder facets
// (scope, deadlines, eligibility, award criteria, ...). Tender-scoped.
// Feature-gated (`facet_analysis`) on the caller's company.
export const getTenderFacetAnalysis = () => `query GetTenderFacetAnalysis($tenderId: String!, $language: String!) {
    tenderFacetAnalysis(tenderId: $tenderId, language: $language) {
        id
        tenderId
        language
        promptVersion
        model
        facets {
            key
            label
            answer
            keyPoints
            hasInfo
            evidence {
                documentId
                documentTitle
                snippet
            }
        }
    }
}`;
